/**
 * GenConfig class to store and manage generation configuration settings
 */
import { FILTER_TYPES } from './buildUtils';
import { peekItemCatalog } from '../services/itemCatalog';
import { POSITIONS, POSITION_NONE } from './st';

/**
 * Items every config starts with in the exclusion list ("Items exclus").
 *
 * Prysmaradites: the solver only sees their stat line (+80 Puissance / +10 % Critique
 * against -1000 Initiative) while their real effect is a spell alteration we do not
 * model, so they outrank almost everything — Prysmenvout sits at #2 in
 * documents/assessment/top_item_scores_breakdown.md. They stay a default rather than a
 * hard block: removing the chip in the panel puts them back in play. Surpryz now
 * also carries an averaged +35 % Critique passive (item_passive_bonuses.py), which
 * only makes it rank higher, so it stays excluded too.
 *
 * Stubs carry what the panel chip and its tooltip read (id, name, icon, effects_map);
 * `id` is core_item.id, which is what getSettings() sends to the solver.
 */
export const DEFAULT_BLACKLIST_ITEMS = [
    {
        id: 22001,
        name: { fr: 'Surpryz', en: 'Surpryz', de: 'Pryberraschung', es: 'Prisorpresa', pt: 'Surprisma' },
        icon: '/media/items/217011.png',
        level: 200,
        item_type_id: 217,
        item_set_id: null,
        effects_map: { 18: 10, 44: -1000 },
    },
    {
        id: 22020,
        name: { fr: 'Prysmenvout', en: 'Prywitchment', de: 'Pryszauberung', es: 'Prisortilegio', pt: 'Prismencanto' },
        icon: '/media/items/217030.png',
        level: 200,
        item_type_id: 217,
        item_set_id: null,
        effects_map: { 25: 80, 44: -1000 },
    },
];

/**
 * Former defaults, taken back from every config that received them.
 *
 * Impétueux (30453) and Pugiliste (30454) were excluded because a request could
 * not say whether the character fights at range or in contact. The
 * None/Mêlée/Distance toggle says it now: with none declared their +6% counts
 * for nothing, with a side declared it counts for exactly what it is worth.
 *
 * Handing a default out copied it into the saved blacklist, so dropping it from
 * the list above changes nothing for existing configs. A config that still has
 * one of these in `blacklistDefaultsApplied` got it from us: it leaves the
 * blacklist and the bookkeeping together, once, and an item the user adds back
 * by hand afterwards stays.
 */
const RETIRED_BLACKLIST_DEFAULT_IDS = [30453, 30454];

/**
 * A default is offered to a config exactly once, ever: the ids already handed out are
 * remembered in `blacklistDefaultsApplied`, so an existing saved config picks up the
 * defaults on its next load (that is what makes them apply to everyone, not only to
 * sessions started after the release), a later addition to the list reaches everyone the
 * same way, and an item the user removed by hand never comes back.
 */
/**
 * Resolve a stored blacklist / locked-slot entry to an item object.
 *
 * Saved configs store item *ids*; older ones still hold whole item objects, and
 * both shapes arrive here. An id that the catalog cannot resolve — because the
 * catalog has not loaded yet, or no longer carries that item — becomes a bare
 * `{ id }` stub rather than nothing at all.
 *
 * The stub matters more than it looks. Every mutator rebuilds the config from
 * `toObject()`, so an entry dropped here would be dropped from the *saved*
 * config by the next unrelated edit. A stub renders as a chip with no name or
 * icon for the moment before the catalog lands, and survives.
 */
function resolveConfigItem(entry) {
    if (entry == null) return null;
    const id = typeof entry === 'number' ? entry : Number(entry?.id);
    if (!Number.isFinite(id) || id <= 0) {
        // No id at all: a number that is not one is nothing, an object without
        // one is whatever it was and is left alone.
        return typeof entry === 'number' ? null : entry;
    }
    // The catalog wins whenever it has the item, for stored objects as much as
    // for ids. A stub written back by an autosave that ran before the catalog
    // loaded is an *object* on the next read, so passing objects through
    // untouched would leave that chip nameless for good; the same rule also
    // refreshes item copies stored before the catalog existed.
    const fromCatalog = peekItemCatalog()?.itemById.get(id);
    if (fromCatalog) return fromCatalog;
    return typeof entry === 'number' ? { id } : entry;
}

function mergeBlacklistDefaults(blacklist, applied) {
    const retired = new Set(RETIRED_BLACKLIST_DEFAULT_IDS.filter(id => applied.includes(id)));
    const kept = retired.size
        ? blacklist.filter(b => !retired.has(b.ankama_id || b.id))
        : blacklist;
    const stillApplied = applied.filter(id => !retired.has(id));
    const alreadyApplied = new Set(stillApplied);
    const present = new Set(kept.map(b => b.ankama_id || b.id));
    const additions = DEFAULT_BLACKLIST_ITEMS.filter(
        it => !alreadyApplied.has(it.id) && !present.has(it.id)
    );
    return {
        blacklist: additions.length ? [...kept, ...additions] : kept,
        applied: [...new Set([...stillApplied, ...DEFAULT_BLACKLIST_ITEMS.map(it => it.id)])],
    };
}

const CFG_KEY_ALLOW_POWER = 'allowPower';
const CFG_KEY_CRIT_DAM_COUNTS_AS_DAMAGE = 'critDamCountsAsDamage';
// The same setting's name until 2026-09-30, still in saved builds and stored defaults.
export const LEGACY_CFG_KEY_CRIT_DAM = 'allowCritDam';
/**
 * Whether the picked boosts count as natural stats of the character in the
 * solve and in the score shown beside the criteria (Build.getSolverBoostEffects),
 * separately for the two kinds of criteria (the settings' Boosts section):
 *
 * - stat criteria ("Utiliser les boosts pour atteindre les critères de stats"),
 *   off by default: a sheet already holding 26 % Critique from boosts asks less
 *   of its gear, which reads as the generator ignoring a target unless asked;
 * - spell criteria ("Prendre en compte les boosts dans le calcul des critères de sort"),
 *   on by default: a spell hits with the boosts the player casts before it.
 */
const CFG_KEY_USE_BOOSTS_FOR_STATS = 'useBoostsForStats';
const CFG_KEY_USE_BOOSTS_FOR_SPELLS = 'useBoostsForSpells';
const CFG_KEY_DISPLAY_PASSIVE_BONUSES = 'displayPassiveBonuses';
/**
 * "Afficher les bonus passifs sur le stuff" is retired while the Boosts tab
 * covers item passives (a picked passive there, the sheet's defaults here, and
 * the two would stack). Off, the setting reads false on every build whatever
 * it stored, and its checkbox is hidden; on, both come back as they were.
 */
export const DISPLAY_PASSIVE_BONUSES_AVAILABLE = false;
/** Stats/spells panels show the per-criterion weight column. Off by default. */
const CFG_KEY_DISPLAY_WEIGHTS = 'displayWeights';
const CFG_KEY_ALLOW_LEGENDARY = 'allowLegendary';
/** AP and MP exos may only go on a ring (solver: `ExoNeeds::rings_only`). Off by default. */
const CFG_KEY_EXO_RINGS_ONLY = 'exoRingsOnly';
/** Persisted UI note (infos panel); not sent to the solver. */
export const BUILD_NOTE_MAX_LEN = 8000;
export const DISPLAY_MODE_GENERATOR = 'generator';
export const DISPLAY_MODE_SIMPLE = 'simple';
export const DEFENSE_SHARE_MIN = 0;
export const DEFENSE_SHARE_MAX = 0.9;
export const DEFENSE_SHARE_STEP = 0.05;
/** Default defense slice for balanced / PVP-style requests. */
export const DEFENSE_SHARE_ON_DEFAULT = 0.5;

/** Defense's share of the combined score (0 = off, 0.9 = 90% defense). */
export function normalizeDefenseShare(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return DEFENSE_SHARE_MIN;
    const clamped = Math.min(DEFENSE_SHARE_MAX, Math.max(DEFENSE_SHARE_MIN, n));
    return Math.round(clamped / DEFENSE_SHARE_STEP) * DEFENSE_SHARE_STEP;
}

/** Extra slider steps when the request shape is harder than raw count suggests. */
const SUGGEST_BUMP_CAP = 3;

/**
 * Steps *down* SEARCH_SUGGEST_STEPS for low-level requests, as [maxLevel, steps].
 * Items carry a level requirement, so the pool a level-60 request searches is a
 * fraction of the level-200 one and converges far sooner — the criteria count
 * alone reads it as the same amount of work. First band whose maxLevel covers
 * the build wins; levels above the last band step down by 0.
 */
const SUGGEST_LEVEL_STEPS_DOWN = [
    [60, 3],
    [100, 2],
    [150, 1],
    [200, 0],
];

/**
 * Default duration for find-the-best-weapon, by the cores the browser reports,
 * as [minCores, seconds]: first row the machine reaches wins.
 *
 * That request runs as a tournament across workers (one per core, up to 8), so
 * what it needs is set by the machine rather than the request's shape. Measured
 * at ~180k evaluations a second per worker: eight or four workers reach the
 * best build known in 5 s, so 7 leaves room for cores slower than the ones
 * measured; one or two gain little from more search on the same candidates,
 * but a longer run widens the first round, which is where they lose the best
 * weapon (it ranks ~9th before searching and they search the first 4-7).
 */
const BEST_WEAPON_SECONDS_BY_CORES = [
    [8, 7],
    [4, 10],
    [0, 15],
];

/** Cores the browser reports; 4 when it does not say. */
function reportedCores() {
    const n = Number(globalThis.navigator?.hardwareConcurrency);
    return Number.isFinite(n) && n > 0 ? n : 4;
}

class GenConfig {
    // User-facing expected wall (natural steps). Shortest stop (5s) is still
    // mostly overhead (~4s) with SEARCH_BUDGET_MIN_S left for search; longer
    // stops are rare manual pushes.
    static TIME_LIMIT_STEPS = [5, 7, 10, 15, 20, 30, 45, 60, 90];
    static TIME_LIMIT_MIN = 5;
    static TIME_LIMIT_MAX = 90;
    static TIME_LIMIT_STEP = 1;   // slider moves by index, not by seconds
    // Approximate expected wall. Search budget sent to the API is derived via
    // searchBudgetSeconds — a rough overhead peel, not exact.
    static TIME_LIMIT_DEFAULT = 10;

    /**
     * Warm-path overhead split (seconds). Defaults from refine-skip local A/B
     * (~construct 2–3s, finalize+delivery ~1–2s). Prefer GenerationRecord
     * `construct_ms` / `finalize_ms` means once prod has samples:
     *   WARM_PRE_S  ≈ mean(construct_ms)/1000
     *   WARM_TAIL_S ≈ mean(finalize_ms)/1000 + ~0.5 delivery
     */
    static WARM_PRE_S = 2.5;
    static WARM_TAIL_S = 1.5;
    static get WARM_OVERHEAD_S() {
        return GenConfig.WARM_PRE_S + GenConfig.WARM_TAIL_S;
    }

    /** Floor for the search budget after peeling warm overhead from a wall pick. */
    static SEARCH_BUDGET_MIN_S = 2;

    /**
     * Internal search ladder for auto-suggest (not shown on the slider).
     * The bottom two rungs exist for SUGGEST_LEVEL_STEPS_DOWN — without them the
     * ladder floors at 4s, which is 8s of wall after overhead and snaps to 7s,
     * so no amount of stepping down could ever suggest the 5s stop. 2 is not
     * arbitrary: it is SEARCH_BUDGET_MIN_S.
     */
    static SEARCH_SUGGEST_STEPS = [2, 3, 4, 5, 7, 10, 15, 20, 30];

    /**
     * Solver `time_limit` from a user-facing wall duration (natural step).
     * Peels warm overhead so the slider stays on human wall numbers while
     * search gets the remainder. Does not snap onto TIME_LIMIT_STEPS.
     */
    static searchBudgetSeconds(wallS) {
        const wall = GenConfig.snapTimeLimit(wallS);
        const raw = wall - GenConfig.WARM_OVERHEAD_S;
        return Math.max(GenConfig.SEARCH_BUDGET_MIN_S, Math.round(raw));
    }

    /**
     * Progress-bar phase boundaries from expected time shares.
     * @param {number} wallS user-facing duration (approx wall)
     * @returns {{ preEnd: number, searchEnd: number, preExpectS: number, tailExpectS: number, searchS: number }}
     */
    static gaugeBands(wallS) {
        const wall = GenConfig.snapTimeLimit(wallS);
        const search = GenConfig.searchBudgetSeconds(wall);
        const pre = GenConfig.WARM_PRE_S;
        const tail = GenConfig.WARM_TAIL_S;
        const total = Math.max(pre + search + tail, 1e-6);
        return {
            preEnd: pre / total,
            searchEnd: (pre + search) / total,
            preExpectS: pre,
            tailExpectS: tail,
            searchS: search,
        };
    }

    /**
     * Gauge bands for a browser-side solve.
     *
     * Local solving spends the whole budget searching: prepare and finalize
     * cost ~0.4s together and sit outside it. So there is no pre band and no
     * tail band — the fill tracks searching from 0 to full, and the phases
     * either side change only what the button says.
     *
     * @param {number} wallS user-facing duration (seconds)
     */
    static localGaugeBands(wallS) {
        const wall = GenConfig.snapTimeLimit(wallS);
        return {
            preEnd: 0,
            searchEnd: 1,
            preExpectS: 0,
            tailExpectS: GenConfig.WARM_TAIL_S,
            searchS: wall,
        };
    }

    /** Nearest allowed stop to an arbitrary value (legacy configs, AI drafts). */
    static snapTimeLimit(seconds) {
        const v = Number(seconds);
        if (!Number.isFinite(v)) return GenConfig.TIME_LIMIT_DEFAULT;
        return GenConfig.TIME_LIMIT_STEPS.reduce(
            (best, s) => (Math.abs(s - v) < Math.abs(best - v) ? s : best),
            GenConfig.TIME_LIMIT_STEPS[0],
        );
    }

    static timeLimitIndex(seconds) {
        const snapped = GenConfig.snapTimeLimit(seconds);
        return Math.max(0, GenConfig.TIME_LIMIT_STEPS.indexOf(snapped));
    }

    /**
     * Auto duration (approx wall) from request shape: light search base from
     * criterion count, then bump a few SEARCH_SUGGEST_STEPS for harder shapes.
     * Result is snapped onto TIME_LIMIT_STEPS (wall figures).
     *
     * Find-the-best-weapon is the exception: its duration follows the machine
     * (`BEST_WEAPON_SECONDS_BY_CORES`), not the request.
     *
     * @param {{ criteriaCount?: number, spellCount?: number, weaponCount?: number, bestWeapon?: boolean, defenseShare?: number, cores?: number }} opts
     * @returns {number} seconds on TIME_LIMIT_STEPS (wall-ish)
     */
    static suggestTimeLimit(opts = {}) {
        if (opts.bestWeapon) {
            const cores = Number(opts.cores) > 0 ? Number(opts.cores) : reportedCores();
            return BEST_WEAPON_SECONDS_BY_CORES.find(([minCores]) => cores >= minCores)[1];
        }
        const criteria = Math.max(0, Math.floor(Number(opts.criteriaCount)) || 0);
        const weapons = Math.max(0, Math.floor(Number(opts.weaponCount)) || 0);
        const defense = normalizeDefenseShare(opts.defenseShare);
        const level = Math.max(0, Math.floor(Number(opts.level)) || 0);

        let searchBase = 4;
        if (criteria <= 4) searchBase = 4;
        else if (criteria <= 8) searchBase = 5;
        else if (criteria <= 14) searchBase = 7;
        else searchBase = 10;

        let bumps = 0;
        if (weapons > 0) bumps += 1;
        if (defense > 0) bumps += 1;
        bumps = Math.min(SUGGEST_BUMP_CAP, bumps);

        // Level steps down the same ladder the bumps step up, so the two compose
        // and a heavy low-level request still outranks a trivial one.
        const band = SUGGEST_LEVEL_STEPS_DOWN.find(([maxLevel]) => level <= maxLevel);
        const stepsDown = level > 0 && band ? band[1] : 0;

        const ladder = GenConfig.SEARCH_SUGGEST_STEPS;
        const baseIdx = ladder.reduce(
            (bestI, s, i) => (Math.abs(s - searchBase) < Math.abs(ladder[bestI] - searchBase) ? i : bestI),
            0,
        );
        const idx = Math.max(0, Math.min(ladder.length - 1, baseIdx + bumps - stepsDown));
        const searchSec = ladder[idx];
        return GenConfig.snapTimeLimit(searchSec + GenConfig.WARM_OVERHEAD_S);
    }

    constructor(config = {}) {
        const rawLimit = config.timeLimit ?? GenConfig.TIME_LIMIT_DEFAULT;
        this.timeLimit = GenConfig.snapTimeLimit(rawLimit);
        // A locked slot holds an item, or `true` for a locked *empty* slot.
        // Only the first shape is stored as an id and needs resolving.
        this.lockedSlots = {};
        for (const [slot, entry] of Object.entries(config.lockedSlots || {})) {
            this.lockedSlots[slot] = entry === true ? true : resolveConfigItem(entry);
        }
        const merged = mergeBlacklistDefaults(
            config.blacklist ? config.blacklist.map(resolveConfigItem).filter(Boolean) : [],
            Array.isArray(config.blacklistDefaultsApplied) ? config.blacklistDefaultsApplied : [],
        );
        this.blacklist = merged.blacklist;
        this.blacklistDefaultsApplied = merged.applied;
        this.passiveOverrides = config.passiveOverrides ? { ...config.passiveOverrides } : {};
        this.displayMode = config.displayMode === DISPLAY_MODE_SIMPLE
            ? DISPLAY_MODE_SIMPLE
            : DISPLAY_MODE_GENERATOR;
        this.defenseShare = normalizeDefenseShare(config.defenseShare);
        // None/Mêlée/Distance: which positional damage% the solver and the spell
        // cards count. Anything unknown reads as "none", today's behaviour.
        this.position = POSITIONS.includes(config.position) ? config.position : POSITION_NONE;

        // Other filters
        this.allowPower = config[CFG_KEY_ALLOW_POWER] !== undefined ? config[CFG_KEY_ALLOW_POWER] : true;
        this.critDamCountsAsDamage = (config[CFG_KEY_CRIT_DAM_COUNTS_AS_DAMAGE] ?? config[LEGACY_CFG_KEY_CRIT_DAM]) === true;
        this.useBoostsForStats = config[CFG_KEY_USE_BOOSTS_FOR_STATS] === true;
        this.useBoostsForSpells = config[CFG_KEY_USE_BOOSTS_FOR_SPELLS] !== false;
        this.allowLegendary = config[CFG_KEY_ALLOW_LEGENDARY] !== undefined ? config[CFG_KEY_ALLOW_LEGENDARY] : true;
        this.exoRingsOnly = config[CFG_KEY_EXO_RINGS_ONLY] === true;
        this.displayPassiveBonuses = DISPLAY_PASSIVE_BONUSES_AVAILABLE
            && config[CFG_KEY_DISPLAY_PASSIVE_BONUSES] === true;
        this.displayWeights = config[CFG_KEY_DISPLAY_WEIGHTS] === true;

        const rawNote = typeof config.buildNote === 'string' ? config.buildNote : '';
        this.buildNote = rawNote.length > BUILD_NOTE_MAX_LEN
            ? rawNote.slice(0, BUILD_NOTE_MAX_LEN)
            : rawNote;

        // Initialize type filters from TYPES array
        this.typeFilters = {};
        if (FILTER_TYPES) {
            for (const type of FILTER_TYPES) {
                this.typeFilters[type.id] = true;
            }
        }

        // Override with any provided typeFilters config
        if (config.typeFilters) {
            this.typeFilters = { ...this.typeFilters, ...config.typeFilters };
        }
    }

    /**
     * Update a specific configuration setting
     * @param {string} key - The configuration key to update
     * @param {any} value - The new value
     * @returns {GenConfig} - New instance with updated configuration
     */
    update(key, value) {
        return new GenConfig({ ...this.toObject(), [key]: value });
    }

    /**
     * Toggles an item in the blacklist. Adds if absent, removes if present.
     * @param {Object} item - The item to toggle.
     * @returns {GenConfig} A new GenConfig instance.
     */
    toggleBlacklistItem(item) {
        const key = item.ankama_id || item.id;
        const exists = this.blacklist.some(b => (b.ankama_id || b.id) === key);
        const newList = exists
            ? this.blacklist.filter(b => (b.ankama_id || b.id) !== key)
            : [...this.blacklist, item];
        return this.update('blacklist', newList);
    }

    /**
     * Adds one or more items to the blacklist, skipping any already present.
     * @param {Object|Object[]} items - A single item or an array of items.
     * @returns {GenConfig} A new GenConfig instance (or this instance if nothing changed).
     */
    addBlacklistItems(items) {
        const list = Array.isArray(items) ? items : [items];
        const existing = new Set(this.blacklist.map(b => b.ankama_id || b.id));
        const additions = [];
        for (const it of list) {
            const key = it && (it.ankama_id || it.id);
            if (!key || existing.has(key)) continue;
            existing.add(key);
            additions.push(it);
        }
        if (additions.length === 0) return this;
        return this.update('blacklist', [...this.blacklist, ...additions]);
    }

    /**
     * Toggles whether a slot is locked for generation.
     * @param {number} slotId - The ID of the slot to toggle.
     * @param {Object|null} item - The item in the slot, or null if empty.
     * @returns {GenConfig} A new GenConfig instance with the updated lock status.
     */
    toggleLockedSlot(slotId, item) {
        const newLockedSlots = { ...this.lockedSlots };
        if (newLockedSlots[slotId]) {
            delete newLockedSlots[slotId]; // Unlock slot
        } else {
            newLockedSlots[slotId] = item || true; // Lock slot, storing item or true if empty
        }
        return this.update('lockedSlots', newLockedSlots);
    }

    /**
     * Toggle a boolean configuration setting
     * @param {string} key - The configuration key to toggle
     * @returns {GenConfig} - New instance with toggled configuration
     */
    toggle(key) {
        if (typeof this[key] !== 'boolean') {
            throw new Error(`Cannot toggle non-boolean property: ${key}`);
        }
        return this.update(key, !this[key]);
    }

    /**
     * Toggles a filter for a specific item type.
     * @param {number} typeId - The ID of the item type from the TYPES array.
     * @returns {GenConfig} A new GenConfig instance with the toggled filter.
     */
    toggleTypeFilter(typeId) {
        const newFilters = { ...this.typeFilters };
        newFilters[typeId] = !newFilters[typeId];
        return this.update('typeFilters', newFilters);
    }

    /**
     * Get the configuration as a plain object for API requests
     * @returns {Object} - Configuration object
     */
    toObject() {
        return {
            timeLimit: this.timeLimit,
            [CFG_KEY_ALLOW_POWER]: this.allowPower,
            [CFG_KEY_CRIT_DAM_COUNTS_AS_DAMAGE]: this.critDamCountsAsDamage,
            [CFG_KEY_USE_BOOSTS_FOR_STATS]: this.useBoostsForStats,
            [CFG_KEY_USE_BOOSTS_FOR_SPELLS]: this.useBoostsForSpells,
            [CFG_KEY_ALLOW_LEGENDARY]: this.allowLegendary,
            [CFG_KEY_EXO_RINGS_ONLY]: this.exoRingsOnly,
            [CFG_KEY_DISPLAY_PASSIVE_BONUSES]: this.displayPassiveBonuses,
            [CFG_KEY_DISPLAY_WEIGHTS]: this.displayWeights,
            parameter2: this.parameter2,
            parameter3: this.parameter3,
            typeFilters: { ...this.typeFilters },
            lockedSlots: { ...this.lockedSlots },
            blacklist: [...(this.blacklist || [])],
            blacklistDefaultsApplied: [...(this.blacklistDefaultsApplied || [])],
            passiveOverrides: { ...(this.passiveOverrides || {}) },
            displayMode: this.displayMode,
            defenseShare: this.defenseShare,
            position: this.position,
            buildNote: this.buildNote || '',
        };
    }

    /**
     * Get settings object for API requests (excludes timeLimit).
     * blacklist is serialized as an array of ankama_ids.
     * @returns {Object} - Settings object for generation API
     */
    getSettings() {
        const {
            timeLimit,
            blacklist: bl,
            buildNote: _omitBuildNote,
            blacklistDefaultsApplied: _omitDefaultsMarker,
            ...settings
        } = this.toObject();
        return {
            ...settings,
            blacklist: (bl || []).map(b => b.ankama_id || b.id).filter(Boolean),
        };
    }

    /**
     * Create a GenConfig instance from a plain object
     * @param {Object} config - Configuration object
     * @returns {GenConfig} - New GenConfig instance
     */
    static fromObject(config) {
        return new GenConfig(config);
    }
}

export default GenConfig; 
